import { ReactElement } from "react";
import { Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { disableDemoMode } from "./demoMode";

/**
 * Component that renders a demo mode message with a login link
 * This component should be used inside Alert or Typography components
 */
interface DemoModeMessageProps {
  message: string;
  onLoginClick?: () => void;
}

export function DemoModeMessage({ message, onLoginClick }: DemoModeMessageProps): ReactElement {
  const navigate = useNavigate();

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    disableDemoMode();
    if (onLoginClick) {
      onLoginClick();
    } else {
      navigate("/login");
    }
  };

  return (
    <>
      {message}{" "}
      <Link
        component="button"
        variant="body2"
        onClick={handleLoginClick}
        sx={{
          textDecoration: "underline",
          fontWeight: "bold",
          cursor: "pointer",
          color: "inherit",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        Log in
      </Link>
      {" to continue."}
    </>
  );
}

/**
 * Hook that returns a function to create demo mode messages with login links
 * Use this inside components to create messages for snackbars/alerts
 */
export function useDemoModeMessage(): (message: string) => ReactElement {
  return (message: string) => <DemoModeMessage message={message} />;
}
